import { memo } from 'react';
import PersonLabel from './PersonLabel';
import type { Person, ChartSettings, Selection } from '../types';

interface PersonNodeProps {
  px: number;
  py: number;
  refX: number;
  refY: number;
  person: Person;
  gi: number;
  pi: number;
  ki: number;
  isSelected: boolean;
  onSelect: (selection: Selection) => void;
  settings: ChartSettings;
}

function PersonNode({ px, py, refX, refY, person, gi, pi, ki, isSelected, onSelect, settings }: PersonNodeProps) {
  const R = settings.dotSize;
  const ariaLabel = person.vacant
    ? `Vacant seat — ${person.role}. Open record.`
    : `${person.name}, ${person.role}. Open record.`;
  // Build the selection from this node's own props instead of receiving a
  // pre-bound closure — lets the parent pass one stable `onSelect` reference
  // to every PersonNode, which is required for React.memo below to be effective.
  const handleSelect = () => onSelect({ kind: 'person', gi, pi, ki });
  return (
    <g
      className={`node${isSelected ? ' on' : ''}`}
      data-gi={gi} data-pi={pi} data-ki={ki}
      tabIndex={0} role="button" aria-label={ariaLabel}
      onClick={handleSelect}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(); } }}
    >
      <circle className={`sel${person.vacant ? ' vac' : ''}`} cx={px} cy={py} r={R + 16} />
      {person.vacant ? (
        <>
          <circle className="vacring" cx={px} cy={py} r={R + 12} />
          <circle className="dot vac" cx={px} cy={py} r={R} />
        </>
      ) : (
        <circle className={`dot${person.lead ? ' lead' : ''}`} cx={px} cy={py} r={R} />
      )}
      <PersonLabel person={person} px={px} py={py} refX={refX} refY={refY} settings={settings} />
    </g>
  );
}

export default memo(PersonNode);
